import {
  Box,
  Button,
  Card,
  Dialog,
  Stack,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ReactElement, useState, useEffect, useCallback } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  TextField,
  useTranslate,
  FunctionField,
  useNotify,
  NumberField,
  DeleteButton,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getLeaderboardCycles,
  createLeaderboardCycle,
  updateLeaderboardCycle,
  deleteLeaderboardCycle,
  LeaderboardCycle,
  CycleStatus,
} from '../../../services/redpacketService';
import CreateLeaderboardCycle from './Create';
import EditLeaderboardCycle from './Edit';
import TopUsersDialog from './TopUsersDialog';
import RewardTiersDialog from './RewardTiersDialog';

const LeaderboardCyclesList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();
  const [data, setData] = useState<LeaderboardCycle[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<CycleStatus | ''>('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const filter = statusFilter ? { status: statusFilter as CycleStatus } : undefined;
      const result = await getLeaderboardCycles(filter);
      setData(Array.isArray(result.cycles) ? result.cycles : []);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify, statusFilter,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    if (window.confirm(translate('module.redpacket.confirmDeleteCycle'))) {
      try {
        await deleteLeaderboardCycle(id);
        notify(translate('module.redpacket.deletedSuccessfully'), { type: 'success' });
        fetchData();
      } catch (error: any) {
        notify(error.message || translate('module.redpacket.deleteFailed'), { type: 'error' });
      }
    }
  };

  const formatTransferCompleted = (v: string | number | null | undefined) => {
    if (v == null || v === '') return '-';
    const d = typeof v === 'number' ? new Date(v) : new Date(String(v));
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  };

  const getStatusColor = (status: CycleStatus) => {
    switch (status) {
      case CycleStatus.ACTIVE:
        return 'success';
      case CycleStatus.UPCOMING:
        return 'info';
      case CycleStatus.COMPLETED:
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.leaderboardCycles')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.leaderboardCycles') },
          ]}
          action={
            <PermissionGuard resource="redpacket" action="create" fallback={<></>}>
              <CreateButton
                variant="contained"
                size="medium"
                onClick={(e) => {
                  e.preventDefault();
                  setPopup(
                    <CreateLeaderboardCycle
                      onClose={() => {
                        setPopup(undefined);
                        fetchData();
                      }}
                    />
                  );
                }}
              />
            </PermissionGuard>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ p: 2 }}>
            <FormControl fullWidth sx={{ mb: 2, maxWidth: 300 }}>
              <InputLabel>{translate('module.redpacket.filterByStatus')}</InputLabel>
              <Select
                value={statusFilter}
                label={translate('module.redpacket.filterByStatus')}
                onChange={(e) => setStatusFilter(e.target.value as CycleStatus | '')}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={CycleStatus.UPCOMING}>Upcoming</MenuItem>
                <MenuItem value={CycleStatus.ACTIVE}>Active</MenuItem>
                <MenuItem value={CycleStatus.COMPLETED}>Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid rowClick={false} data={data} isLoading={loading} bulkActionButtons={false}>
              <NumberField source="cycleNumber" label={translate('module.redpacket.cycleNumber')} />
              <FunctionField
                label={translate('module.redpacket.startTime')}
                render={(record: LeaderboardCycle) => {
                  const date = record.startTime;
                  return date ? new Date(date).toLocaleString() : '-';
                }}
              />
              <FunctionField
                label={translate('module.redpacket.endTime')}
                render={(record: LeaderboardCycle) => {
                  const date = record.endTime;
                  return date ? new Date(date).toLocaleString() : '-';
                }}
              />
              <FunctionField
                label={translate('module.redpacket.status')}
                render={(record: LeaderboardCycle) => (
                  <Chip label={record.status} color={getStatusColor(record.status)} size="small" />
                )}
              />
              <FunctionField
                label={translate('module.redpacket.rewardAutoTransferEnabled')}
                render={(record: LeaderboardCycle) => (
                  <Chip
                    size="small"
                    label={
                      record.rewardAutoTransferEnabled
                        ? translate('module.redpacket.yes')
                        : translate('module.redpacket.no')
                    }
                    color={record.rewardAutoTransferEnabled ? 'success' : 'default'}
                    variant="outlined"
                  />
                )}
              />
              <FunctionField
                label={translate('module.redpacket.rewardTransferTime')}
                render={(record: LeaderboardCycle) => {
                  const date = record.rewardTransferTime;
                  return date ? new Date(date).toLocaleString() : '-';
                }}
              />
              <FunctionField
                label={translate('module.redpacket.rewardTransferStatus')}
                render={(record: LeaderboardCycle) =>
                  record.rewardTransferStatus ? (
                    <Chip label={record.rewardTransferStatus} size="small" variant="outlined" />
                  ) : (
                    '-'
                  )
                }
              />
              <FunctionField
                label={translate('module.redpacket.rewardTransferCompletedAt')}
                render={(record: LeaderboardCycle) =>
                  formatTransferCompleted(record.rewardTransferCompletedAt)
                }
              />
              <FunctionField
                label={translate('module.redpacket.createdAtCol')}
                render={(record: LeaderboardCycle) => {
                  const date = record.createdAt;
                  return date ? new Date(date).toLocaleString() : '-';
                }}
              />
              <FunctionField
                label={translate('module.redpacket.operation')}
                render={(record: LeaderboardCycle) => (
                  <Stack direction="row" spacing={1}>
                    <PermissionButton resource="redpacket" action="read">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPopup(
                            <TopUsersDialog
                              record={record}
                              onClose={() => setPopup(undefined)}
                            />
                          );
                        }}
                      >
                        {translate('module.redpacket.topUsers')}
                      </Button>
                    </PermissionButton>
                    <PermissionButton resource="redpacket" action="update">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPopup(
                            <RewardTiersDialog
                              record={record}
                              onClose={() => setPopup(undefined)}
                            />
                          );
                        }}
                      >
                        {translate('module.redpacket.configReward')}
                      </Button>
                    </PermissionButton>
                    {(record?.status === CycleStatus.UPCOMING ||
                      record?.status === CycleStatus.ACTIVE) && (
                      <PermissionButton resource="redpacket" action="update">
                        <EditButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setPopup(
                              <EditLeaderboardCycle
                                id={record.id}
                                record={record}
                                onClose={() => {
                                  setPopup(undefined);
                                  fetchData();
                                }}
                              />
                            );
                          }}
                        />
                      </PermissionButton>
                    )}
                    {record?.status === CycleStatus.UPCOMING && (
                      <PermissionButton resource="redpacket" action="delete">
                        <DeleteButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(record.id);
                          }}
                        />
                      </PermissionButton>
                    )}
                  </Stack>
                )}
              />
            </Datagrid>
          </Box>
        </Card>

        {popup && (
          <Dialog open maxWidth="md" fullWidth>
            {popup}
          </Dialog>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default LeaderboardCyclesList;
